import _ from 'lodash';
import { templateCompanyDetailsTab } from '../view/template';
import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { CompanyDetailsTabWebComponent } from '../component/CompanyDetailsTabWebComponent';
import { CompanyDetailsTabView } from '../view/CompanyDetailsTabView';
import { CompanyDetailsTabRepository } from '../repository/CompanyDetailsTabRepository';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { CompanyData } from '../../../types/company-data';
import { EditCompanyPanelEvents } from '../../../editCompany/editCompanyPanel/view/EditCompanyPanelEnums';
import { CompanyType } from '../../../types/company-type';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import { CompanyDetailsTabEvents } from '../types/CompanyDetailsTabEvents';
import { AssignedProductTypeException } from '../../../../license/types/AssignedProductTypeException';
import { OnManageByAboveChange } from '../../companyDetailsForm/types/CompanyDetailsEvents';
import { DeepPartial } from 'typeorm';
import { SubscriptionType } from '../../../../license/types/SubscriptionType';

export class CompanyDetailsTabController extends BaseWebComponentController {
  public component: CompanyDetailsTabWebComponent;
  public view: CompanyDetailsTabView;
  public viewConstructor: typeof CompanyDetailsTabView;
  public dataRepository: CompanyDetailsTabRepository;
  public dataRepositoryConstructor: typeof CompanyDetailsTabRepository;

  constructor(component: CompanyDetailsTabWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompanyDetailsTabView,
      viewTemplate: templateCompanyDetailsTab,
      dataRepositoryConstructor: CompanyDetailsTabRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.dispatchCancelEvent,
      },
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.save,
      },
    ];
  }

  private async save(): Promise<void> {
    this.view.dom.$saveButton?.setLoadingState(true);

    try {
      const companyDetailsFormReference = this.view.dom.$companyDetailsForm;

      if (companyDetailsFormReference) {
        this.dispatchLoadingEvent(true);

        await companyDetailsFormReference.uploadLogo();
        await this.updateCompanyRecord();
        await companyDetailsFormReference.updateCustomFieldsValues();

        companyDetailsFormReference.refreshInitialFormValue();

        companyDetailsFormReference.resetWarnings();

        this.dispatchAfterSaveEvent();
        this.dispatchLoadingEvent(false);

        if (this.component.context === CompanyActionContext.MyCompany) {
          const canBeManagedByAbove = companyDetailsFormReference.getCanBeManagedByAbove();
          this.component.dispatchEvent(new OnManageByAboveChange(canBeManagedByAbove));
          this.component.setEditedCompanyData(this.component.company);
          this.component.setupMdrContactDetailsSection();
        }
      }

      this.showSuccessToast();
    } catch (error) {
      // if the exception is AuthenticationSecurityException it is already treated by the general session exception
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }

      this.showErrorToast(error);
    } finally {
      this.view.dom.$saveButton?.setLoadingState(false);
    }
  }

  private dispatchLoadingEvent(operationInProgress: boolean) {
    if (this.component.context !== CompanyActionContext.Edit) {
      return;
    }

    // Dispatch event only when editing a company
    this.component.dispatchEvent(
      new CustomEvent(EditCompanyPanelEvents.AuthenticationTabOperationInProgress, {
        detail: { isOperationInProgress: operationInProgress },
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private dispatchAfterSaveEvent() {
    // Dispatch event only when editing a company
    this.component.dispatchEvent(
      new CustomEvent(CompanyDetailsTabEvents.AfterSave, {
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private dispatchCancelEvent() {
    // Dispatch event only when editing a company
    this.component.dispatchEvent(
      new CustomEvent(CompanyDetailsTabEvents.Cancel, {
        bubbles: true,
        cancelable: true,
        composed: false,
      })
    );
  }

  private async getCompanyFromRepository(): Promise<CompanyData> {
    if (this.component.context === CompanyActionContext.Edit) {
      return this.dataRepository.getManagedCompanyById(this.component.company._id);
    }

    return this.dataRepository.getMyCompanyData();
  }

  private async updateCompanyRecord(): Promise<void> {
    // need to merge the form values with the existing company records because it's required by the API
    const companyDetailsFormValue = this.view.dom.$companyDetailsForm.value;
    const dbRecord = await this.getCompanyFromRepository();

    const updatedRecord = _.merge({}, dbRecord, companyDetailsFormValue);

    this.unsetSubscriptionEndDateIfInherited(updatedRecord);
    this.unsetMdrContactInformationIfNotNeeded(updatedRecord, companyDetailsFormValue);
    this.changeFeatureFlagsIfNeeded(updatedRecord, dbRecord);

    await this.dependencies.api.clients.Company.SaveRecord(updatedRecord).toPromise();

    // update cached record in view
    this.component.company = updatedRecord;
    // reset the value of the logo form field (so that it uses the one saved in db)
    this.view.dom.$companyDetailsForm?.resetLogoField();
  }

  private showSuccessToast() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.dependencies.translateFn('Company details updated successfully.'),
      severity: ToastSeverity.SUCCESS,
    });
  }

  private changeFeatureFlagsIfNeeded(updatedRecord: CompanyData, dbRecord: CompanyData) {
    if (this.component.context !== CompanyActionContext.Edit) {
      return;
    }
    const oldCompanyType = CompanyData.getCompanyType(dbRecord);
    const newCompanyType = CompanyData.getCompanyType(updatedRecord);

    // if company type is not changed, do nothing
    if (oldCompanyType === newCompanyType) {
      return;
    }

    if (newCompanyType === CompanyType.Customer) {
      // partner -> customer
      // disable all additional product type delegate flags
      for (const productType of MonthlyProductType.getAllProductTypes()) {
        const featureName = MonthlyProductType.getDelegateFeatureNameForProductType(productType);
        updatedRecord.specifics.features[featureName] = false;
      }
    } else {
      // customer -> partner
      // enable the appropriate additional product type delegate flag
      const productType = MonthlyProductType.getAssignedProduct(dbRecord.specifics.features);
      const featureName = MonthlyProductType.getDelegateFeatureNameForProductType(productType);
      updatedRecord.specifics.features[featureName] = true;
    }
  }

  private showErrorToast(error: Error) {
    let errorMessage;

    if (
      error['exceptionType'] === 'Exceptions\\License\\AssignedProductTypeException' &&
      error['code'] === AssignedProductTypeException.CODE_DEFAULT_PRODUCT_TYPE_CANNOT_BE_ASSIGNED
    ) {
      errorMessage = this.dependencies.translateFn(
        'The assigned product type is no longer available for this company. To change it, go to the Licensing tab and select Edit licensing options. Otherwise, cancel the changes.'
      );
    } else {
      errorMessage = error?.message || this.translator.getGenericErrorMessage();
    }

    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    });
  }

  async getDataForView() {
    return {
      context: this.component.context,
    };
  }

  private unsetSubscriptionEndDateIfInherited(updatedRecord: CompanyData) {
    const baseLicense = CompanyData.getFirstBaseLicense(updatedRecord);

    if (!baseLicense) {
      return;
    }

    const shouldUpdateInheritedData = !baseLicense.endSubscription?.endDateIsSet;

    // Unset endSubscription node because company details save must not affect subscription availability
    if (shouldUpdateInheritedData) {
      _.unset(updatedRecord, ['specifics', 'licenses', '0', 'endSubscription']);
    }
  }

  private unsetMdrContactInformationIfNotNeeded(
    updatedRecord: CompanyData,
    companyDetailsFormValue: DeepPartial<CompanyData & { subscription: SubscriptionType; addUser: boolean }>
  ) {
    const mdrContactInfo = _.get(companyDetailsFormValue, ['specifics', 'mdrContactInformation'], null);

    if (!mdrContactInfo) {
      // Make sure that mdrContactInformation is sent only when displayed in the form
      _.unset(updatedRecord, ['specifics', 'mdrContactInformation']);
    }
  }
}
